import { getProductFormLabel } from '@/lib/productFormData';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, FileText, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useBulkImport } from '@/contexts/BulkImportContext';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';
import type { ImportProposal } from './streamChat';
import { cn } from '@/lib/utils';
import { saveCardOutcome } from '@/lib/chatCards';

export const FEATURE_META: FeatureMeta = {
  id: 'chat-listing-import',
  label: 'Create listings from a chat file',
  description:
    'When you attach an inventory file in Ask Cannafax and ask for listings, a card lets you confirm; Cannafax then creates the listings in your inventory — complete rows go live, anything missing details is flagged for review.',
  triggerType: 'action',
  personas: ['seller', 'buyer_seller'],
};

/** Plain-language progress copy; backend phase names never reach the screen. */
export function chatImportStage(status: string, phase?: string): string {
  if (status === 'completed') return 'Listings ready';
  if (status === 'failed') return "Couldn't finish";
  if (status === 'pending' || status === 'uploading') return 'Queued';
  switch (phase) {
    case 'downloading': return 'Receiving your file';
    case 'analyzing':
    case 'understanding': return 'Reading your file';
    case 'extracting': return 'Identifying products';
    case 'saving': return 'Creating listings';
    default: return 'Working on it';
  }
}

type State = 'offer' | 'uploading' | 'processing' | 'done' | 'failed' | 'dismissed';
interface Draft { id: string; strain_name: string | null; product_form: string | null }

export function ChatImportCard({ proposal, file, threadId }: { proposal: ImportProposal; file?: File; threadId?: string | null }) {
  useFeatureRegistration(FEATURE_META);
  const { user } = useAuth();
  const bulk = useBulkImport();
  const saved = proposal.outcome;
  const [state, setState] = useState<State>(() =>
    saved && saved.state !== 'uploading' ? (saved.state as State) : 'offer');
  const [uploadId, setUploadId] = useState<string | null>(saved?.uploadId ?? null);
  const [error, setError] = useState<string | null>(saved?.error ?? null);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [draftCount, setDraftCount] = useState(saved?.draftCount ?? 0);
  const finished = useRef(false);

  // Reopened while still importing: resume following the same job.
  useEffect(() => {
    if (saved?.state === 'processing' && saved.uploadId && bulk.uploadId !== saved.uploadId) bulk.startImport(saved.uploadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the conversation's record of this card current.
  useEffect(() => {
    if (state === 'offer' || state === 'uploading') return;
    void saveCardOutcome(threadId, proposal.cardId, { state, uploadId, draftCount, error });
  }, [state, uploadId, draftCount, error, threadId, proposal.cardId]);

  const mine = uploadId && bulk.uploadId === uploadId;
  const progress = mine ? bulk.progress : null;

  useEffect(() => {
    if (state !== 'processing' || !mine || !bulk.isComplete || finished.current) return;
    finished.current = true;
    if (bulk.isError) {
      setError(progress?.processing_error || 'Something went wrong while reading this file.');
      setState('failed');
      return;
    }
    const ids = progress?.listing_ids ?? [];
    setDraftCount(ids.length || progress?.listings_created || 0);
    setState('done');
    if (ids.length) {
      void supabase
        .from('listings')
        .select('id, strain_name, product_form')
        .in('id', ids.slice(0, 8))
        .then(({ data }) => setDrafts((data as Draft[]) ?? []));
    }
  }, [state, mine, bulk.isComplete, bulk.isError, progress]);

  const confirm = async () => {
    if (!user || !file) return;
    setState('uploading');
    setError(null);
    try {
      const { data: me } = await supabase.from('users').select('company_id').eq('id', user.id).single();
      const companyId = me?.company_id;
      if (!companyId) throw new Error('Your account is not linked to a company yet.');
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${companyId}/chat-${Date.now()}/${safe}`;
      const { error: upErr } = await supabase.storage.from('bulk_uploads').upload(path, file);
      if (upErr) throw new Error('The file could not be uploaded. Please try again.');
      const { data, error: fnErr } = await supabase.functions.invoke('bulk-import', {
        body: { file_path: path, company_id: companyId, user_id: user.id, source: 'chat' },
      });
      if (fnErr || data?.error || !data?.upload_id) throw new Error(data?.message || 'The import could not be started. Please try again.');
      setUploadId(data.upload_id);
      bulk.startImport(data.upload_id);
      setState('processing');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Please try again.');
      setState('failed');
      toast.error('Import did not start', { description: e instanceof Error ? e.message : undefined });
    }
  };


  const pct = progress?.processing_progress?.total
    ? Math.round(((progress.processing_progress.current ?? 0) / progress.processing_progress.total) * 100)
    : null;

  return (
    <div
      className={cn('mt-2 w-full rounded-xl border border-border bg-card p-4 text-sm shadow-sm', state === 'dismissed' && 'opacity-60')}
      aria-disabled={state === 'dismissed' || undefined}
    >
      <div className="flex items-start gap-3">
        <FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-foreground">{proposal.fileName}</p>
          <p className="text-muted-foreground">{proposal.summary}</p>
        </div>
      </div>

      {state === 'offer' && (
        <>
          {proposal.missing.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              Not found in the file: {proposal.missing.join(', ')}. You can add these on each listing later.
            </p>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            {file
              ? 'Complete listings go straight into your active inventory; anything missing a name or quantity is flagged for review. Buyers only see listings you add to a shared menu.'
              : 'The file is no longer attached. Attach it again and ask me to import it.'}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" onClick={confirm} disabled={!file}>
              Create {proposal.productsDetected ? `${proposal.productsDetected} ` : ''}listings
            </Button>
            <Button size="sm" variant="quiet" onClick={() => setState('dismissed')}>Not now</Button>
          </div>
        </>
      )}

      {(state === 'uploading' || state === 'processing') && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span>
              {state === 'uploading'
                ? 'Sending your file'
                : chatImportStage(progress?.processing_status ?? 'pending', progress?.processing_progress?.phase)}
              {progress?.processing_progress?.message ? ` · ${progress.processing_progress.message}` : ''}
            </span>
          </div>
          {pct !== null && <Progress value={pct} className="h-1.5" />}
          <p className="text-xs text-muted-foreground">You can keep working — we'll let you know when your listings are ready.</p>
        </div>
      )}

      {state === 'done' && (
        <div className="mt-3 space-y-2">
          <p className="flex items-center gap-2 font-medium text-foreground">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            {draftCount} listing{draftCount === 1 ? '' : 's'} added to your inventory
          </p>
          {drafts.length > 0 && (
            <ul className="space-y-1">
              {drafts.map((d) => (
                <li key={d.id}>
                  <Link to={`/listings/${d.id}/edit`} className="text-primary underline-offset-2 hover:underline">
                    {d.strain_name || 'Untitled listing'}
                  </Link>
                  {d.product_form && <span className="text-muted-foreground"> · {getProductFormLabel(d.product_form)}</span>}
                </li>
              ))}
            </ul>
          )}
          <Button size="sm" asChild>
            <Link to="/my-listings">View in Inventory</Link>
          </Button>
        </div>
      )}

      {state === 'dismissed' && (
        <p className="mt-3 text-xs text-muted-foreground">Not imported — you chose not now.</p>
      )}

      {state === 'failed' && (
        <div className="mt-3 space-y-2">
          <p className="flex items-start gap-2 text-destructive">
            <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            {error}
          </p>
          {!uploadId && file && <Button size="sm" variant="quiet" onClick={confirm}>Try again</Button>}
        </div>
      )}
    </div>
  );
}
